<script setup lang="ts">
import { ref } from 'vue'
import { NInput } from 'naive-ui'
import type { SidebarSearch } from '../composables/useSidebarSearch'

// Reusable wiki-search input + min-chars hint. Hosts pass a
// `useSidebarSearch` instance so they keep ownership of the search
// state (allows independent queries on the sidebar vs HomeLanding)
// AND control over where the results panel mounts. This component
// only owns:
//   - the `<NInput>` shape (placeholder, accessibility, mobile
//     keyboard hints)
//   - the "Type 2+ characters" hint shown below the input while
//     `query.length > 0` and `isSearchMode` is false
//   - a `blur()` method exposed to the host so it can dismiss the
//     soft keyboard after a result tap
//
// Render the results separately via `<WikiSidebarSearchResults>` —
// gated on the same `isSearchMode` ref the host already has.

const props = withDefaults(
  defineProps<{
    // Shared composable instance. The widget reads `query` (two-way) and
    // `isSearchMode` (read-only) — every other field is for the host.
    search: SidebarSearch
    placeholder?: string
    ariaLabel?: string
    // Naive UI input size. WikiSidebar uses `small` to fit the dense
    // catalog rail; HomeLanding leaves the default for a roomier
    // landing input.
    size?: 'tiny' | 'small' | 'medium' | 'large'
  }>(),
  {
    placeholder: 'Search brain...',
    ariaLabel: 'Search the brain',
    size: 'medium',
  },
)

// Destructure to drop the `props.search.X.value` chain in the
// template — Vue's template compiler auto-unwraps top-level refs in
// the setup scope. The host owns the composable instance for its
// own lifetime, so the ref identity here is stable.
const { query, isSearchMode } = props.search

const inputRef = ref<InstanceType<typeof NInput> | null>(null)

defineExpose({
  blur(): void {
    inputRef.value?.blur?.()
  },
})
</script>

<template>
  <div class="flex flex-col gap-1" data-testid="wiki-search-panel">
    <NInput
      ref="inputRef"
      v-model:value="query"
      :placeholder="props.placeholder"
      clearable
      :size="props.size"
      :input-props="{ inputmode: 'search', enterkeyhint: 'search', 'aria-label': props.ariaLabel }"
    >
      <!-- Surface the command-palette shortcut so ⌘K stops being
           invisible. Non-interactive hint; hidden once the user starts
           typing (clearable's ✕ owns the suffix then) and on touch
           viewports where there's no keyboard shortcut. -->
      <template v-if="!query.length" #suffix>
        <kbd
          data-testid="palette-hint"
          class="hidden sm:inline-block text-[10px] leading-none text-white/40 border border-white/15 rounded px-1 py-0.5 select-none"
          title="Open the command palette"
          >⌘K</kbd
        >
      </template>
    </NInput>
    <!-- A single character looks broken without this hint — the FTS
         query only fires at SEARCH_MIN_CHARS, so without feedback the
         user thinks the search is dead. -->
    <p
      v-if="query.length > 0 && !isSearchMode"
      class="text-[11px] text-white/40 m-0 px-0.5"
      data-testid="wiki-search-hint"
    >
      Type 2+ characters to search
    </p>
  </div>
</template>
